import { withAbortTimeout } from './async';

type SupabaseOperationResult = { error: unknown };

type RetryOptions = {
  maxAttempts?: number;
  baseDelayMs?: number;
  sleep?: (delayMs: number) => Promise<void>;
  signal?: AbortSignal;
  timeoutMs?: number;
};

const errorText = (reason: unknown) => {
  if (reason instanceof Error) return reason.message;
  if (typeof reason === 'string') return reason;
  if (reason && typeof reason === 'object') {
    const value = reason as Record<string, unknown>;
    return [value.code, value.message, value.details, value.hint]
      .filter((part): part is string => typeof part === 'string')
      .join(' ');
  }
  return '';
};

export const isTransientDatabaseError = (reason: unknown) => (
  /57014|canceling statement due to statement timeout|statement timeout|timed out|timeout|failed to fetch|network|temporarily unavailable|\b50[234]\b/i
    .test(errorText(reason))
);

const defaultSleep = (delayMs: number) => new Promise<void>((resolve) => setTimeout(resolve, delayMs));

export async function withDatabaseRetry<T extends SupabaseOperationResult>(
  operation: (signal: AbortSignal) => PromiseLike<T>,
  options: RetryOptions = {},
): Promise<T> {
  const maxAttempts = Math.max(1, options.maxAttempts ?? 2);
  const baseDelayMs = Math.max(0, options.baseDelayMs ?? 200);
  const sleep = options.sleep ?? defaultSleep;

  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    options.signal?.throwIfAborted();
    try {
      const result = await withAbortTimeout(operation, options.timeoutMs ?? 12_000, options.signal);
      if (!result.error || !isTransientDatabaseError(result.error) || attempt === maxAttempts) return result;
    } catch (reason) {
      options.signal?.throwIfAborted();
      if (!isTransientDatabaseError(reason) || attempt === maxAttempts) throw reason;
    }
    await sleep(baseDelayMs * attempt);
  }

  throw new Error('database_retry_exhausted');
}
